import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import {
  useAuthentication,
  useExportData,
  useRetrieveCurrentPlanzone,
  useProjectResourcesRetrieval,
  useRetrieveProjectResourcesListing,
  useRetrieveResourceGroups,
  useRetrieveProjectMembersListingConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import {
  ExportsDataTypes,
  ExportsFormats,
  ResourcesListingFilter,
  ResourcesListingSort,
} from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { usePreferences } from "@components/preferences";
import { useModal } from "@root/client/modal";
import { DragAndDrop } from "@common/dragAndDrop";
import { Menu } from "./menu";
import { ResourcesList } from "./resourcesList";
import { GroupsList } from "./groupsList";
import { SideBar } from "./sidebar";
import { ToggleViewButton } from "./toggleViewButton";
import { Filters } from "./filters";
import { Filter as F, Props, Sorters } from "./types";
import { ExportButton } from "./exportButton";
import styles from "./styles.scss";

const PAGE_SIZE = 50;

const listingSorts: { [Key in Sorters]: ResourcesListingSort } = {
  [Sorters.None]: ResourcesListingSort.Default,
  [Sorters.Name]: ResourcesListingSort.Name,
  [Sorters.Role]: ResourcesListingSort.Role,
  [Sorters.EntryDate]: ResourcesListingSort.CreationDate,
};

function ResourcesPage({ project }: Props) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const { account } = useAuthentication();
  const { exportData } = useExportData();
  const { t } = useTranslation();
  const { state, edit } = usePreferences();
  const { projectResourcesFilter: filter } = state;
  const setFilter = (nextFilter: F) =>
    edit({ ...state, projectResourcesFilter: nextFilter });
  const [sort, setSort] = useState<Sorters>(Sorters.None);
  const {
    projectResources,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval(project);
  const {
    projectResources: listedProjectResources,
    filter: listingFilter,
    totalPages,
    isRequestPending: isListingRequestPending,
    retrieveProjectResourcesListing,
  } = useRetrieveProjectResourcesListing(project);
  const listingCriteria: ResourcesListingFilter = {
    page: 0,
    size: PAGE_SIZE,
    resourceTypes: filter.resourceTypes,
    roles: filter.roles,
    inactiveOnly: filter.inactiveOnly,
    activeOnly: filter.activeOnly,
    organizationalUnits: filter.organizationalUnits,
    resources: filter.resources,
    groups: filter.groups,
    resourceTags: filter.resourceTags,
    sort: listingSorts[sort],
  };
  const { groups, isRequestPending: isRequestGroupPending } =
    useRetrieveResourceGroups(project.uuid);
  const { pushView } = useModal();
  const { configuration } =
    useRetrieveProjectMembersListingConfiguration(planzone);
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "resource",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const fixedColumns = ["tags", "role"];
  const role = account.resource?.role;
  const matchedProfile = configuration.find(
    (profile) =>
      !!role && profile.roles.find((r) => r === role.name || r === role.uuid),
  );
  const columns = matchedProfile
    ? matchedProfile.columns.filter(
        ({ key }) =>
          fixedColumns.includes(key) ||
          template.find(({ uuid }) => uuid === key),
      )
    : [];

  React.useEffect(() => {
    !!project.uuid &&
      !isRequestPending &&
      !isRequestFailure &&
      !isRequestSuccess &&
      retrieveProjectResources(project);
  }, [project.uuid]);

  React.useEffect(() => {
    !!project.uuid &&
      retrieveProjectResourcesListing({ project, filter: listingCriteria });
  }, [project.uuid, JSON.stringify(listingCriteria)]);

  // Everyone added by the back is listed again, in the order and with the filters of the page.
  useMessage(({ type, data }) => {
    type === events.addEveryoneToProjectSucceeded &&
      data.project.uuid === project.uuid &&
      retrieveProjectResourcesListing({ project, filter: listingCriteria });
  });

  return (
    <DragAndDrop>
      <div className={styles.resourcesPage}>
        <Helmet>
          <title>Planzone • {t("projectResourcesPage.title")}</title>
          <meta name="description" content={project.description} />
        </Helmet>
        <div className={styles.filters}>
          <Filters
            filter={filter}
            sort={sort}
            setFilter={setFilter}
            setSort={setSort}
          />
          <div className={styles.viewFilter}>
            <div className={styles.toggleViewButton}>
              <p>{t("projectResourcesPage.toggleViewButton")}</p>
              <ToggleViewButton
                areSortedByGroups={filter.areSortedByGroups}
                setAreSortedByGroup={() =>
                  setFilter({
                    ...filter,
                    areSortedByGroups: !filter.areSortedByGroups,
                  })
                }
              />
            </div>
            <Menu project={project} />
          </div>
        </div>
        <div className={styles.main}>
          <div className={styles.sidebar}>
            <SideBar
              project={project}
              createProjectGroup={() =>
                pushView({
                  key: UseCases.addResourceGroup,
                  data: {
                    namespace: project.uuid,
                    resourceType: "project",
                  },
                })
              }
              filter={filter}
              setFilter={setFilter}
            />
          </div>
          {filter.areSortedByGroups ? (
            <GroupsList
              groups={groups}
              project={project}
              filter={filter}
              sort={sort}
              isRequestPending={isRequestGroupPending || isRequestPending}
              columns={columns}
            />
          ) : (
            <ResourcesList
              projectResources={listedProjectResources}
              project={project}
              isRequestPending={isListingRequestPending}
              canLoadMore={listingFilter.page < totalPages}
              retrieveNextResources={() =>
                retrieveProjectResourcesListing({
                  project,
                  filter: listingFilter,
                })
              }
              columns={columns}
            />
          )}
        </div>
        <div className={styles.exportButton}>
          <ExportButton
            export={() => {
              exportData({
                fileName: t("projectResourcesPage.workbookName") + project.name,
                author: account,
                data: {
                  type: ExportsDataTypes.RESOURCES_REPORT,
                  data: {
                    planzone,
                    project,
                    filter: listingCriteria,
                  },
                },
                format: ExportsFormats.EXCEL,
              });
            }}
          />
        </div>
      </div>
    </DragAndDrop>
  );
}

export { ResourcesPage };
